import { isArray, isDate } from 'lodash';
import 'whatwg-fetch';
import { getHost } from '../helper/getHost';
import store from '../store/configureStore';
import { ObjectFormService } from '../services/ObjectFormService';
import { getTemplatesObjects } from '../reducers/objectReducers/action';

export const ipServer = `https://${getHost()}`;

const fetchWithInstanceId = (url, options = {}) => {
  const state = store.getState();
  const instanceId = state.auth.instanceId;

  const separator = url.includes('?') ? '&' : '?';
  const urlWithId = `${url}${separator}instanceId=${instanceId}`;

  return fetch(urlWithId, options);
};

/**
 * @namespace ObjectApi
 */
const ObjectApi = {
  /** Ask the backend which user is currently logged into the
   * @public
   * @returns {promise} JSON Object with userId and email property
   */
  getWhosLoggedOnServer() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getWhosLoggedOnServer`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  /** Ask the backend to verify the token provided on client side by the provider.
   * @param {string} idToken
   * @param {string} provider
   */
  checkToken(idToken, provider) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=checkToken&idToken=${idToken}&idProvider=${provider}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  /** Ask the backend to delete the current server session */
  logout() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=logout`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  checkLogin(login, password) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=checkLogin&login=${login}&password=${password}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getAIAsk(question, objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getAIAsk`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, objId }),
      }
    ).then((response) => response.json());
  },

  refreshAISynthesis(objId, fieldName, localInstruction) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=refreshAISynthesis&objId=${objId}&fieldName=${fieldName}&localInstruction=${localInstruction}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  cleanAISynthesis(objId, fieldName) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=cleanAISynthesis&objId=${objId}&fieldName=${fieldName}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  fetchObjects(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getChildObjectList&parent=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  /** Ask the server to associate the provided file to the node identified by objId
   * @param {string} objId the target node id
   * @param {file} file the uploaded file
   * @param {string} filename the original file name
   * @param {string} attrName the node field name the file will be associated to (ex: contract)
   */
  postFile(objId, file, filename, attrName) {
    try {
      const formData = new FormData();
      if (file) formData.append('file', file, filename);
      return fetchWithInstanceId(
        `${ipServer}/guerillaPM3/DynAction?requ=postFile&filename=${encodeURIComponent(
          filename
        )}&objId=${objId}&attrName=${attrName}`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          body: formData,
        }
      ).then((response) => response.json());
    } catch (error) {}
  },

  getExtractFromExcelFile(
    file,
    filename,
    page,
    overrides = null,
    options = {},
    valueSelect,
    selectedFramework
  ) {
    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file, filename);
      }
      if (overrides) {
        formData.append('overrides', JSON.stringify(overrides));
      }
      if (selectedFramework) {
        formData.append('selectedFramework', JSON.stringify(selectedFramework));
      }
      if (valueSelect) {
        formData.append('typeToCreate', valueSelect);
      }
      if (options?.import === true) {
        formData.append('import', 'true');
      }
      if (options?.resetOverrides === true) {
        formData.append('resetOverrides', 'true');
      }
      formData.append('includeDataPreview', 'true');
      return fetchWithInstanceId(
        `${ipServer}/guerillaPM3/DynAction?requ=getExtractFromExcelFile&filename=${encodeURIComponent(
          filename
        )}&pageNumber=${page}`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          body: formData,
        }
      ).then((response) => response.json());
    } catch (error) {}
  },

  getPreviewFromExcelFile(
    mapping,
    savedFileReference,
    page,
    objectTypeToImport
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getPreviewFromExcelFile&savedFileReference=${encodeURIComponent(
        savedFileReference
      )}&pageNumber=${page}&objectTypeToImport=${objectTypeToImport}`,
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: mapping,
      }
    ).then((response) => response.json());
  },

  postTemplateFile(objId, file, filename, attrName) {
    const formData = new FormData();
    if (file) formData.append('file', file, filename);
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=postTemplateFile&filename=${encodeURIComponent(
        filename
      )}&objId=${objId}&attrName=${attrName}`,
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        body: formData,
      }
    ).then((response) => response.json());
  },

  /** Save JSON icons and images library to the server and retrun filepath of the file */
  saveLibrary(content) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=saveLibrary`,
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },

        body: content,
      }
    ).then((response) => response.json());
  },

  getIconsLibrary() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DownloadFile/download/library/library.json`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: '*',
        },
      }
    ).then((response) => response.json());
  },

  /** Save JSON icon or image for library one the server and retrun filepath of the file */
  saveLibraryAsset(content, filename) {
    const formData = new FormData();
    formData.append('file', content, filename);
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=saveLibraryAsset&filename=${encodeURIComponent(
        filename
      )}`,
      {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: formData,
      }
    ).then((response) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok ${response.statusText}`);
      }
      return response.json();
    });
  },

  addNodeToReadLayer(layerId, nodeId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=addNodeToReadLayer&nodeId=${nodeId}&layerId=${layerId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  addNodeToWriteLayer(layerId, nodeId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=addNodeToWriteLayer&nodeId=${nodeId}&layerId=${layerId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  removeNodeFromReadLayer(layerId, nodeId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=removeNodeFromReadLayer&nodeId=${nodeId}&layerId=${layerId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  removeNodeFromWriteLayer(layerId, nodeId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=removeNodeFromWriteLayer&nodeId=${nodeId}&layerId=${layerId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  applyAudienceRightsToChilds(nodeId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=applyAudienceRightsToChilds&nodeId=${nodeId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  saveObject(objId, paramJSON, signal) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=saveObject&objId=${objId}`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paramJSON),
        signal,
      }
    ).then((response) => response.json());
  },

  socialToggleIcon(objId, iconName) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=socialToggleIcon&objId=${objId}&iconName=${iconName}`,
      {
        credentials: 'include',
        method: 'GET',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
      }
    ).then((response) => response.json());
  },

  getSocialContent(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getSocialContent&objId=${objId}`,
      {
        credentials: 'include',
        method: 'GET',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
      }
    ).then((response) => response.json());
  },

  socialPostComment(objId, comment) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=socialPostComment&objId=${objId}&comment=${comment}`,
      {
        credentials: 'include',
        method: 'GET',
        // mode: 'no-cors',
        // headers: { 'Content-Type': 'application/json' },
        headers: { 'Content-Type': 'text/plain' },
      }
    ).then((response) => response.json());
  },

  sendState(stringState, roomId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=sendState`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: stringState,
      }
    ).then((response) => response.json());
  },

  pingServer() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=pingServer`,
      {
        credentials: 'include',
        method: 'GET',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
      }
    ).then((response) => response.json());
  },

  getState(stringState, roomId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getState&roomId=${roomId}`,
      {
        credentials: 'include',
        method: 'GET',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: stringState,
      }
    ).then((response) => response.json());
  },

  voiceToText(objId, attrName, file) {
    const formData = new FormData();
    if (file)
      formData.append('audio', file, `${objId}-${attrName}recording.wav`);
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=executeCommand&command=voicetotext&objId=${objId}&attrName=${attrName}`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      }
    ).then((response) => response.json());
  },

  voiceToTextInput(file) {
    const formData = new FormData();
    if (file) formData.append('audio', file, `recording.wav`);

    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=executeCommand&command=voicetotextInput`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      }
    ).then((response) => response.json());
  },

  saveObjectAttribute(
    objId,
    key,
    attrName,
    label,
    defaultValue,
    options,
    displayByDefault,
    allowMultiple,
    required,
    type,
    icon,
    order,
    formulaType,
    formulaObject,
    formulaField,
    formulaCondition,
    formulaConditionJSON,
    formulaCat1,
    formulaCat2,
    tabName,
    refValue,
    maxValue,
    regex,
    instructions,
    readOnly,
    unit,
    canReadGroups,
    canWriteGroups,
    synonyms,
    translations,
    valueTransform,
    isBackRef
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=saveObjectAttribute`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objId,
          key,
          attrName,
          label,
          defaultValue,
          options,
          displayByDefault,
          allowMultiple,
          required,
          type,
          icon,
          order,
          formulaType,
          formulaObject,
          formulaField,
          formulaCondition,
          formulaConditionJSON,
          formulaCat1,
          formulaCat2,
          tabName,
          refValue,
          maxValue,
          regex,
          instructions,
          readOnly,
          unit,
          canReadGroups,
          canWriteGroups,
          synonyms,
          translations,
          valueTransform,
          isBackRef,
        }),
      }
    ).then((response) => response.json());
  },
  exportObject(objId, format = 'xlsx', exportHierarchy = false) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=exportObject`,
      {
        credentials: 'include',
        method: 'POST',
        // disable CORS if you want get data from local
        // mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objId: [...(isArray(objId) ? [...objId] : [objId])],
          format,
          exportHierarchy,
        }),
      }
    ).then((response) => {
      response.json().then((res) => {
        const link = document.createElement('a');
        link.href = `${ipServer}/guerillaPM3/DownloadFile/download/${res.file}/${res.file}`;
        link.click();
      });
    });
  },

  deleteObject(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=deleteObject&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getUserRole() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getUserRole`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getUserData() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getUserData`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getUserPrivs() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getUserPrivs`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  updateLayoutDocument(objId, fldName) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=updateLayoutDocument&objId=${objId}&fieldName=${fldName}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  selectObject(objId) {
    if (objId == null || objId.length == 0) return;

    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getObjAttributesList&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  getObjDefList() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getObjDefList`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  getChildsCountByType(arrayParentsId, level) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getChildsCountByType`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level,
          arrayParentsId,
        }),
      }
    )
      .then((response) => response.json())
      .catch((err) => {});
  },

  fetchDrilldown(
    objId,
    formulaCat1,
    formulaCat2,
    cat1,
    cat2,
    extraCondition,
    skip,
    limit
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getDrilldown`,
      {
        credentials: 'include',
        method: 'POST',
        // attention : "no-cors" bloque souvent la lecture de la réponse → si tu veux lire JSON, mets "cors"
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objId,
          formulaCat1,
          formulaCat2,
          cat1,
          cat2,
          extraCondition,
          skip,
          limit,
        }),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        return response.json();
      })
      .catch((err) => {
        console.error('fetchDrilldown error', err);
        return { ids: [], total: 0 };
      });
  },
  //------------------------------------------------------------------------------------------------
  updateObjectDefinition(
    objId,
    label,
    description,
    icon,
    allowUnder,
    naming = 'manual',
    isSystem,
    systemCategory,
    canReadGroups,
    canWriteGroups,
    canCreateGroups
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=updateObjectDefinition`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objId,
          label,
          description,
          icon,
          allowUnder: JSON.stringify(allowUnder),
          naming,
          isSystem,
          systemCategory,
          canReadGroups,
          canWriteGroups,
          canCreateGroups,
        }),
      }
    )
      .then((response) => {
        return response;
      })
      .catch((err) => {
        console.log(
          '🚀 ~ file: ObjectApi.js:409 ~ updateObjectDefinition ~ err:',
          err
        );
      });
  },

  //------------------------------------------------------------------------------------------------
  saveObjectDefinition(objId, description, inherit) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=saveObjectDefinition&objId=${objId}&description=${description}&inherit=${inherit}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  deleteObjectDefinition(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=deleteObjectDefinition&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------
  executeCommand(command, objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=executeCommand&objId=${objId}&command=${command}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------
  suggestWorkflow(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=suggestWorkflow&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  suggestModel(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=suggestModel&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------
  implementModel(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=implementModel&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------
  deleteObjectAttribute(objAttrId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=deleteObjectAttribute&objAttrId=${objAttrId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },
  //------------------------------------------------------------------------------------------------
  /**
   * Ask backend to create a brother copy of the current node (child ol the same node)
   * @public
   * @param   {string} objId     src node
   * @param   {boolean} [copyChilds=false] copyChilds define if the node childs need to be copied too
   * @returns {promise} the new object Id
   */
  createBrother(objId, copyChilds = false, nbCopy = 1) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=createBrother&objId=${objId}&copyChilds=${copyChilds}&nbCopy=${nbCopy}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------
  getFather(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getFather&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------
  getNumberChilds(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getNumberChilds&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  //------------------------------------------------------------------------------------------------

  createFromObjectDefinitionInNode(objId, parentId, perspectiveId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=createFromObjectDefinitionInNode&objId=${objId}&parentId=${parentId}&perspectiveId=${perspectiveId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  createFromObjectDefinitionInNodeOnLayers(
    objId,
    parentId,
    perspectiveId,
    layersRead,
    layersWrite
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=createFromObjectDefinitionInNodeOnLayers`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objId,
          parentId,
          layersRead,
          layersWrite,
          perspectiveId,
        }),
      }
    ).then((response) => response.json());
  },

  createFromTemplateObjectInNodeOnLayers(
    templateObjectId,
    parentId,
    perspectiveId,
    layersRead,
    layersWrite
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=createFromTemplateObjectInNodeOnLayers`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateObjectId,
          parentId,
          layersRead,
          layersWrite,
          perspectiveId,
        }),
      }
    ).then((response) => response.json());
  },

  draggedOnNode(objId, parentId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=draggedOnNode&objId=${objId}&parentId=${parentId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  search(term) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=search&term=${term}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getPerspectives() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getPerspectives`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getTemplatesObjects() {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getTemplatesObjects`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  getTreeByPattern(
    pattern,
    qb_restrictions,
    paging_map,
    paging_excluded,
    mandatoryLevel,
    arraySortBy,
    viewFilters,
    viewFiltersRestrictions,
    signal
  ) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getTreeByPattern&pattern=${pattern}`,
      {
        credentials: 'include',
        method: 'POST',
        ...(process.env.NODE_ENV === 'development'
          ? { headers: { 'Content-Type': 'text/plain' } }
          : {
              mode: 'no-cors',
              headers: { 'Content-Type': 'application/json' },
            }),
        body: JSON.stringify({
          qb_restrictions: qb_restrictions,
          paging_map: paging_map ?? {},
          paging_excluded: paging_excluded,
          mandatory_level: mandatoryLevel ?? 0,
          arraySortBy,
          viewFilters,
          viewFiltersRestrictions,
        }),
        signal,
      }
    ).then((response) => response.json());
  },

  getCrumbTrail(objId) {
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getCrumbTrail&objId=${objId}`,
      {
        credentials: 'include',
      }
    ).then((response) => response.json());
  },

  saveObjFormData(objId, objFormData, signal) {
    let paramJSON = ObjectFormService.objFormToJSON(objFormData);
    paramJSON.objId = objId;
    console.log('saveObject_2');

    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=saveObject&objId=${objId}`,
      {
        credentials: 'include',
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paramJSON),
        signal,
      }
    ).then((response) => response.json());
  },
  getActivities({
    skip = 0,
    limit,
    actType,
    time,
    includeChilds,
    objId,
    actAuthor,
  }) {
    const queryString = {
      skip,
      limit,
      actType,
      includeChilds,
      objId,
      ...(actAuthor ? { actAuthor } : {}),
    };
    if (isArray(time)) {
      if (isDate(time[0]) && isDate(time[1])) {
        queryString.dateStart = time[0].getTime();
        queryString.dateEnd = time[1].getTime();
      }
    }
    return fetchWithInstanceId(
      `${ipServer}/guerillaPM3/DynAction?requ=getActivities&${objToQueryString(
        queryString
      )}`,
      {
        credentials: 'include',
        method: 'GET',
        headers: { 'Content-Type': 'text/plain' },
      }
    ).then((response) => response.json());
  },
};
function objToQueryString(obj) {
  const keyValuePairs = [];
  for (const key in obj) {
    if (encodeURIComponent(obj[key]) !== 'null') {
      keyValuePairs.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
      );
    }
  }
  return keyValuePairs.join('&');
}
export default ObjectApi;
